import { FaTicketAlt, FaSearch, FaUserFriends, FaCar } from "react-icons/fa";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

const steps = [
  {
    id: 1,
    title: "Offer a ride",
    icon: <FaSearch />,
    description:
      "Enter departure, destination, date, and car details. Enable AUTOMATIC APPROVAL for instant bookings. Female drivers can offer females-only rides.",
    cta: true,
  },
  {
    id: 2,
    title: "Receive booking requests",
    icon: <FaTicketAlt />,
    description:
      "Passengers book your ride. With automatic approval enabled, requests are accepted instantly. Chat or contact passengers directly.",
  },
  {
    id: 3,
    title: "Start the ride",
    icon: <FaUserFriends />,
    description:
      "Meet passengers, collect payment before departure, and start the ride from your dashboard.",
  },
  {
    id: 4,
    title: "Complete & rate",
    icon: <FaCar />,
    description:
      "Mark the ride as complete after reaching the destination and leave feedback for passengers.",
  },
];

const HowItWorksForDriver = () => {
  const navigate = useNavigate();

  return (
    <Section>
      <Header>
        <span>Earn. Drive. Share.</span>
        <h2>How GoSawari Works for Drivers</h2>
      </Header>

      <Grid>
        {steps.map((step) => (
          <Card key={step.id}>
            <StepNumber>{step.id}</StepNumber>

            <IconWrapper>{step.icon}</IconWrapper>

            <h3>{step.title}</h3>
            <p>{step.description}</p>

            {step.cta && (
              <CTA onClick={() => navigate("/login")}>
                Login to Offer a Ride →
              </CTA>
            )}
          </Card>
        ))}
      </Grid>
    </Section>
  );
};

export default HowItWorksForDriver;

const Section = styled.section`
  padding: 5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white; /* Clean light background */

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const Header = styled.div`
  text-align: center;
  max-width: 720px;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }

  span {
    color: #00796b;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    font-size: 0.85rem;

    @media (max-width: 768px) {
      font-size: 0.75rem;
    }
  }

  h2 {
    margin-top: 0.75rem;
    font-size: 2.4rem;
    font-weight: 700;
    line-height: 1.2;
    color: #054752;
    letter-spacing: -0.5px;

    @media (max-width: 1024px) {
      font-size: 2rem;
    }

    @media (max-width: 768px) {
      font-size: 1.8rem;
    }

    @media (max-width: 480px) {
      font-size: 1.6rem;
      line-height: 1.3;
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  max-width: 1200px;
  width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  position: relative;
  padding: 2.6rem 2.2rem;
  border-radius: 22px;
  background: white;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-shadow: 0 4px 10px rgba(0,0,0,0.03);

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(5, 71, 82, 0.1);
    border-color: #00897b;
  }

  h3 {
    margin: 1.3rem 0 0.8rem;
    font-size: 1.25rem;
    font-weight: 600;
    color: #455a64;
  }

  p {
    font-size: 0.96rem;
    line-height: 1.65;
    color: #78909c;
    flex-grow: 1;
  }
`;

const StepNumber = styled.div`
  position: absolute;
  top: 20px;
  right: 22px;
  font-size: 3.6rem;
  font-weight: 800;
  background: linear-gradient(135deg, #e0f2f1, #b2dfdb);
  -webkit-background-clip: text;
  color: #b2dfdb; /* Fallback */
  opacity: 0.6;
`;

const IconWrapper = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00796b, #004d40);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.45rem;
  color: white;
`;

const CTA = styled.button`
  margin-top: 1.6rem;
  padding: 0.85rem 1.6rem;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #00796b, #004d40);
  color: white;
  font-weight: 600;
  cursor: pointer;
  align-self: flex-start;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(0, 77, 64, 0.45);
  }
`;
